import React, { useState, useEffect, useRef, useContext, useMemo } from "react"
import I18n from "@helper/i18nInstance"
import _ from "lodash"
import scrollIntoView from "scroll-into-view-if-needed"

import "./QuestionAnswers.scss"

import { getRandomBadHeadline, getRandomGoodHeadline, onlyUseSeriousAndRespectfulIconsAndTexts } from "@Quiz/helper"
import { useQuiz } from "@Quiz/context"
import { useQuizCard } from "@Quiz/Ui/QuizCard"
import { QuestionHeader } from "@Quiz/Ui"
import Button from "@ui/Button"
import Link from "@ui/Link"
import { Icon } from "@ui/Icon"
import QuizEmbedInfoContext from "@Quiz/QuizEmbedInfoContext"
import { usePreloadImages } from "@helper/usePreloadImages"
import MiniQuestionHeader from "./MiniQuestionHeader"
import MultipleChoiceAnswers, { MultipleChoiceResultInfo } from "./MultipleChoiceAnswers"
import SliderAnswers, { SliderResultInfo } from "./SliderAnswers"
import MultiSelectAnswers, { MultiSelectResultInfo } from "./MultiSelectAnswers"
import { isMatchPairsQuestion, isMultipleChoiceQuestion, isMultiSelectQuestion, isSliderQuestion } from "@api/types"
import MatchPairsAnswers, { MatchPairsResultInfo } from "./MatchPairsAnswers"

interface IAnswersState {
  askCancel: boolean
}

export const usePreloadAnswerImages = (conf: Quiz.Api.Question["config"]) => {
  const optionsToConsider = conf?.type === "multiple_choice"
    ? conf.options
    : undefined
  const imageUrls = useMemo<string[]>(() => {
    if (optionsToConsider) {
      return _.chain(optionsToConsider).filter((opt) => !!opt.image).map((opt) => opt.image?.["1x"]).compact().value()
    }
    return []
  }, [optionsToConsider])

  return usePreloadImages(imageUrls)
}

const QuestionAnswers: React.FC<{showedForFirstTime: boolean, showQuestionAgain(): void}> = ({ showedForFirstTime, showQuestionAgain }) => {
  const { resultIcons } = useContext(QuizEmbedInfoContext)
  const { recalcInnerHeight } = useQuizCard()
  const { quiz, actions } = useQuiz()
  const [ resultOverlayHeadline, setResultOverlayHeadline ] = useState(quiz?.question?.eval?.result?.was_correct ? getRandomGoodHeadline() : getRandomBadHeadline())

  useEffect(() => { // -----------> set result headline
    const randomHeadline = quiz?.question?.eval?.result?.was_correct ? getRandomGoodHeadline() : getRandomBadHeadline()
    let was_timeout = false
    const result = quiz?.question?.eval?.result
    const countdown = quiz?.question?.countdown
    if (result) {
      was_timeout = !!(!result.was_correct && (result.was_timeout || (countdown && countdown.millis_left <= 0)))
    }
    setResultOverlayHeadline(was_timeout ? I18n.t("quiz.question_eval.timeout") : randomHeadline)
  }, [quiz?.question?.countdown, quiz?.question?.eval?.result, quiz?.question?.eval?.result?.was_correct])

  const [ state, setState ] = useState<IAnswersState>({
    askCancel: false
  })
  const fadeIn = showedForFirstTime

  const hasQuestionConfig = !!quiz?.question?.config
  const hasEval = !!quiz?.question?.eval

  useEffect(() => { // --------------- ONCE + AFTER CONFIG + AFTER EVAL -> recalc cardheight
    recalcInnerHeight()
  }, [hasQuestionConfig, hasEval, recalcInnerHeight])

  if (!quiz?.question) { return null }

  const bodyClassName = [
    "neo__questionAnswers__body",
    fadeIn ? "neo__questionAnswers__body--fadeInQuestions" : "",
  ].join(" ")

  const hasOverlay = state.askCancel || quiz?.question?.eval?.result

  return (
    <div className={`neo__questionAnswers${hasOverlay ? " neo__questionAnswers--maxHeightForOverlay" : ""}`} data-testid="quiz-question-answers">
      {hasOverlay && <Overlay>
        {state.askCancel && <>
            <OverlayTitleArea>
              <Icon iconIds={resultIcons.question} />
              <OverlayHeadline variant="question" htmlContent={I18n.t("quiz.actions.restart.confirm")} />
            </OverlayTitleArea>
            <OverlayActionArea>
              <Button onClick={() => setState({...state, askCancel: false})}>{I18n.t("quiz.actions.restart.continue")}</Button>
              <Link onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.actions.restart.cancel_quiz")}</Link>
            </OverlayActionArea>
          </>}
        {!state.askCancel && quiz.question.eval?.result && (() => {
          const result = quiz.question.eval.result
          const correct = result.was_correct
          return (<>
            <OverlayTitleArea>
              {correct ? <Icon iconIds={(onlyUseSeriousAndRespectfulIconsAndTexts(quiz) && resultIcons.positiveRespectful) || resultIcons.positive} /> : <Icon iconIds={resultIcons.negative} />}
              <OverlayHeadline variant={correct ? "good" : "bad"}>{resultOverlayHeadline}</OverlayHeadline>
            </OverlayTitleArea>
            <OverlayContentArea>
              {result.explanation && <div className="neo__questionAnswers__explanation" dangerouslySetInnerHTML={{__html: result.explanation}} />}
              {!result.was_correct && result.result_info && <>
                {isMultipleChoiceQuestion(quiz.question) && <MultipleChoiceResultInfo question={quiz.question} />}
                {isSliderQuestion(quiz.question) &&  <SliderResultInfo question={quiz.question} />}
                {isMultiSelectQuestion(quiz.question) &&  <MultiSelectResultInfo question={quiz.question} />}
                {isMatchPairsQuestion(quiz.question) &&  <MatchPairsResultInfo question={quiz.question} />}
              </>}
            </OverlayContentArea>
            {!result.was_correct && _.isNumber(result.num_wrong_answers_left) && result.has_more_questions && (result.num_wrong_answers_left >= 0) && <div
              className="neo__questionAnswers__triesLeft"
              dangerouslySetInnerHTML={{__html: I18n.t("quiz.question_eval.num_tries_left", {count: result.num_wrong_answers_left})}}/>}
            <OverlayActionArea>
              {result.can_continue
                ? <Button onClick={() => actions.continueQuiz()}>{I18n.t(`quiz.question_eval.${result.has_more_questions ? "next_question" : "eval_quiz"}`)}</Button>
                : <Button onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.quiz_eval.normal_quiz.btn_restart")}</Button>}
              <Link onClick={() => {if (result.can_continue) {setState({...state, askCancel: true})} else {actions.cancelQuiz()}}}>{I18n.t("quiz.actions.restart.cancel_quiz")}</Link>
            </OverlayActionArea>
          </>)})()}
        </Overlay>}
      <div className="neo__questionAnswers__head"><QuestionHeader meta={quiz.meta} /></div>
      <div className={bodyClassName}>
        <div className="neo__questionAnswers__body__inner">
          <MiniQuestionHeader showQuestionAgain={showQuestionAgain} />
          {isMultipleChoiceQuestion(quiz.question) && <MultipleChoiceAnswers />}
          {isSliderQuestion(quiz.question) && <SliderAnswers />}
          {isMultiSelectQuestion(quiz.question) && <MultiSelectAnswers conf={quiz.question.config} tryAgain={quiz.question.eval?.try_again} />}
          {isMatchPairsQuestion(quiz.question) && <MatchPairsAnswers conf={quiz.question.config} tryAgain={quiz.question.eval?.try_again} />}
          {quiz.question.eval?.try_again && <div className="neo__questionAnswers__tryAgain">
            {I18n.t("quiz.question_eval.try_again")}</div>}
        </div>
      </div>
      <div className="neo__questionAnswers__foot">
        <Link onClick={() => setState({...state, askCancel: true})}>{I18n.t("quiz.actions.cancel")}</Link>
      </div>
    </div>
  )
}

const Overlay: React.FC<React.PropsWithChildren> = ({children}) => {
  const { quiz } = useQuiz()
  const overlayRef = useRef<HTMLDivElement>()
  const bodyRef = useRef<HTMLDivElement>()
  const innerRef = useRef<HTMLDivElement>()
  const [ forceInnerHeight, setForceInnerHeight ] = useState<number>()

  useEffect(() => { // set inner height to prevent safari flex bug (items not shrunken otherwise)
    if (bodyRef.current && innerRef.current) {
      const bodyHeight = bodyRef.current.getBoundingClientRect().height
      const innerContentHeight = innerRef.current.getBoundingClientRect().height
      if (innerContentHeight > bodyHeight) {
        setForceInnerHeight(bodyHeight)
      }
    }
  }, [])

  useEffect(() => { // scroll into view if the overlay's top edge is above the visible area
    // (can happen with a tall question, e.g. match_pairs, capped question height + overlay)
    if (overlayRef.current && overlayRef.current.getBoundingClientRect().top < 0) {
      scrollIntoView(overlayRef.current, { behavior: "smooth", scrollMode: "always", block: "start" })
    }
  }, [])

  return (
    <div className="neo__answersOverlay" ref={(r) => {if (r) { overlayRef.current = r }}}>
      {quiz && <div className="neo__answersOverlay__head"><QuestionHeader meta={quiz.meta} /></div>}
      <div ref={(r) => {if (r) { bodyRef.current = r }}} className="neo__answersOverlay__body">
        <div ref={(r) => {if (r) { innerRef.current = r }}} className="neo__answersOverlay__body__inner" style={_.isNumber(forceInnerHeight) ? { height: `${forceInnerHeight}px` } : {}}>
          {children}
        </div>
      </div>
    </div>
  )
}

type THeadlineVariant = "good" | "bad" | "question"
const OverlayHeadline: React.FC<React.PropsWithChildren<{htmlContent?: string, variant: THeadlineVariant}>> = ({variant, children, htmlContent}) => {
  const className = [
    "neo__answerOverlay__headline",
    `neo__answerOverlay__headline--${variant}`
  ].join(" ")

  return htmlContent
    ? <h3 className={className} dangerouslySetInnerHTML={{__html: htmlContent}}/>
    : <h3 className={className}>{children}</h3>
}

const OverlayActionArea: React.FC<React.PropsWithChildren> = ({children}) => {
  return <div className="neo__answerOverlay__actionArea">{children}</div>
}

const OverlayContentArea: React.FC<React.PropsWithChildren> = ({children}) => {
  return <div className="neo__answerOverlay__contentArea">{children}</div>
}

const OverlayTitleArea: React.FC<React.PropsWithChildren> = ({children}) => {
  return <div className="neo__answerOverlay__titleArea">{children}</div>
}

export default QuestionAnswers
